import { computed, toValue } from 'vue';

import { useCommunityPermissions } from '@/app/community/composables/useCommunityPermissions';
import { COMMUNITY_PERMISSION_TYPES } from '@/app/community/constants';
import {
  FILTER_DETAILS,
  SENTIMENT_FILTER_OPTIONS,
} from '@/app/community/constants/communityFilters';
import { formatUser, getCurrentAndUnassignedUser } from '@/app/community/utils/teams';
import {
  MESSAGE_TAG_OPTION_KEYS,
  REPORT_COMMUNITY_FILTERS,
  REPORT_LISTENING_FILTERS,
  REPORT_PANEL_OPTIONS,
} from '@/app/dashboards/reportConstants';
import { getCompetitorList } from '@/app/dashboards/utils/competitive.utils';
import { REPORT_CATEGORY_OPTIONS } from '@/app/dashboards/utils/reports.enum';
import { useListeningPermissions } from '@/app/socialListening/composables/useListeningPermissions';
import {
  MEDIA_TYPE_FILTER_OPTIONS,
  MENTION_TYPE_FILTER_OPTIONS,
  SENTIMENT_FILTER_OPTIONS as LISTENING_SENTIMENT_FILTER_OPTIONS,
} from '@/app/socialListening/constants/listeningFilters';
import { getChannelConfigProp } from '@/models/dashboards/channels.enum';
import { useCommunityMessageTagsStore } from '@/stores/community-message-tags';
import { useCommunityMultiBrandsStore } from '@/stores/community-multi-brands';
import { useDashboardsStore } from '@/stores/dashboards';
import { useDashboardsSocialListeningStore } from '@/stores/dashboards-social-listening';
import { useIdentityStore } from '@/stores/identity';
import { useSearchStore } from '@/stores/search';

import {
  DASHBOARD_COMMUNITY_MESSAGE_TYPES,
  FILTER_TOOLTIPS,
  MESSAGE_TYPE_CONSTANTS,
} from '../constants';

export function useFilterOptions({
  reportCategory,
  selectedChannels,
  selectedBrands,
  selectedBrandIds,
  selectedMessageTypes,
  selectedSentiments,
} = {}) {
  const category = computed(() => toValue(reportCategory));
  const channels = computed(() => toValue(selectedChannels));
  const brandIds = computed(() => {
    if (selectedBrands?.value) {
      return selectedBrands.value.map((brand) => brand.id);
    }
    return toValue(selectedBrandIds) || [];
  });
  const messageTypes = computed(() => toValue(selectedMessageTypes) || []);
  const sentiments = computed(() => toValue(selectedSentiments) || []);
  const filterMap = FILTER_DETAILS.COMMUNITY;
  const messageTagsStore = useCommunityMessageTagsStore();
  const dashboardsStore = useDashboardsStore();
  const communityMultiBrandsStore = useCommunityMultiBrandsStore();
  const identityStore = useIdentityStore();
  const searchStore = useSearchStore();
  const { verifyBrandAndUserAccess } = useCommunityPermissions();
  const { commentsEnabled } = useListeningPermissions();
  const dashboardsSocialListeningStore = useDashboardsSocialListeningStore();

  const disabledMessageTypes = computed(() => {
    const disabledTypes = [];
    const disableTypeTooltip = [];

    filterMap.options.messageTypes.forEach((option) => {
      const platformList = option.insightsPlatforms ?? option.platforms;
      const isSupported =
        channels.value?.length > 0 &&
        channels.value.some((platform) => platformList?.includes(platform));

      if (!isSupported) {
        disabledTypes.push(option.value);
      }

      if (sentiments.value.length > 0 && messageTypes.value.length === 0) {
        const optionToDisable = !MESSAGE_TYPE_CONSTANTS.SENTIMENT_SUPPORTED_MESSAGE_TYPES.includes(
          option.statsValue,
        );
        if (optionToDisable) {
          disabledTypes.push(option.value);
          disableTypeTooltip.push({
            value: option.value,
            description: FILTER_TOOLTIPS.SENTIMENT_MESSAGE_TYPE_DISABLED,
          });
        }
      }
    });

    return { disabledTypes, disableTypeTooltip };
  });

  // TODO: Will need brand ids for permissions checks
  const messageTypesFilterOptions = computed(() => {
    const defaultOptions = filterMap.options.messageTypes;
    const isDmsSelected = messageTypes.value.includes(DASHBOARD_COMMUNITY_MESSAGE_TYPES.DMS);

    return defaultOptions.reduce((acc, { statsValue, value, info, insightsInfo, ...option }) => {
      if (!statsValue) return acc;

      let disabled = disabledMessageTypes.value.disabledTypes.includes(value);
      let description =
        disabledMessageTypes.value.disableTypeTooltip?.find((tooltip) => tooltip.value === value)
          ?.description ||
        insightsInfo ||
        info;

      if (statsValue === DASHBOARD_COMMUNITY_MESSAGE_TYPES.DMS) {
        description = FILTER_TOOLTIPS.DMS_TOOLTIP;
      } else if (statsValue === DASHBOARD_COMMUNITY_MESSAGE_TYPES.STORY_MENTION) {
        description = FILTER_TOOLTIPS.STORY_MENTION_TOOLTIP;
        if (isDmsSelected) {
          disabled = true;
        }
      }

      return [
        ...acc,
        {
          ...option,
          disabled,
          value: statsValue,
          description,
        },
      ];
    }, []);
  });

  const messageTagFilterOptions = computed(() => {
    if (!brandIds.value?.length) {
      return [];
    }

    const tagsByBrand = messageTagsStore.messageTagsByBrand;
    const formattedOptions = [];
    const dashboardBrands = dashboardsStore.dashboardIdentityBrands;

    brandIds.value.forEach((brandId) => {
      const brandMessageTags = tagsByBrand[brandId] || [];
      if (brandMessageTags.length === 0) return;

      const brand = dashboardBrands.find((b) => b.id === brandId);
      if (!brand) return;

      const sortedTags = [...brandMessageTags].sort((a, b) => a.name.localeCompare(b.name));

      formattedOptions.push({
        label: brand.name,
        value: `brand_${brandId}`,
        avatarUrl: brand.avatarUrl,
        type: MESSAGE_TAG_OPTION_KEYS.BRAND,
        children: sortedTags.map((tag) => ({
          label: tag.name,
          value: tag.id,
          color: tag.color,
          ...tag,
        })),
      });
    });

    return formattedOptions.sort((a, b) => a.label.localeCompare(b.label));
  });

  const assigneeFilterOptions = computed(() => {
    if (!brandIds.value?.length) {
      return [];
    }

    const allAssignees = new Map();

    brandIds.value.forEach((brandId) => {
      const brandAssignees =
        communityMultiBrandsStore.brandIdToAccessibleBrandUsersMap[brandId] || [];

      brandAssignees.forEach((user) => {
        const formattedUser = formatUser(user);
        if (!allAssignees.has(formattedUser.value)) {
          const hasValidName = !!(user.firstName?.trim() || user.lastName?.trim());
          if (!hasValidName) {
            formattedUser.label = user.email;
          }
          allAssignees.set(formattedUser.value, formattedUser);
        }
      });
    });

    const currentAndUnassignedUsers = getCurrentAndUnassignedUser(identityStore.identity);

    currentAndUnassignedUsers.forEach((user) => {
      if (user.type !== 'line' && !allAssignees.has(user.value)) {
        if (user.value === -1) {
          allAssignees.set(user.value, user);
        } else {
          const hasValidName = !!(
            identityStore.identity?.firstName?.trim() || identityStore.identity?.lastName?.trim()
          );
          if (hasValidName) {
            allAssignees.set(user.value, user);
          }
        }
      }
    });

    const sortedAssignees = Array.from(allAssignees.values()).sort((a, b) => {
      // unassigned (-1) to appear first
      if (a.value === -1) return -1;
      if (b.value === -1) return 1;

      // Use the label for sorting, which includes the formatted display name
      const aName = a.label || '';
      const bName = b.label || '';
      return aName.localeCompare(bName);
    });

    // divider line after unassigned
    if (sortedAssignees.length > 0 && sortedAssignees[0].value === -1) {
      sortedAssignees.splice(1, 0, { type: 'line', divider: true });
    }

    return sortedAssignees;
  });

  const filterOptions = computed(() => ({
    [REPORT_COMMUNITY_FILTERS.MESSAGE_TYPES]: messageTypesFilterOptions.value,
    [REPORT_COMMUNITY_FILTERS.MESSAGE_TAG_IDS]: messageTagFilterOptions.value,
    [REPORT_COMMUNITY_FILTERS.SENTIMENT]: Object.values(SENTIMENT_FILTER_OPTIONS),
    [REPORT_COMMUNITY_FILTERS.ASSIGNEE_IDS]: assigneeFilterOptions.value,
  }));

  const brandsWithoutSentimentAccess = computed(() => {
    if (selectedBrands?.value) {
      return selectedBrands.value.filter(
        (brand) => !verifyBrandAndUserAccess(brand, COMMUNITY_PERMISSION_TYPES.TEXT_ANALYSIS),
      );
    }
    return [];
  });

  const allBrandsWithoutSentimentAccess = computed(() => {
    if (selectedBrands?.value) {
      return brandsWithoutSentimentAccess.value.length === selectedBrands.value.length;
    }
    return false;
  });
  const messageTypesSupportSentiment = computed(() =>
    messageTypes.value.every((type) =>
      MESSAGE_TYPE_CONSTANTS.SENTIMENT_SUPPORTED_MESSAGE_TYPES.includes(type),
    ),
  );

  const isSentimentFilterDisabled = computed(
    () => allBrandsWithoutSentimentAccess.value || !messageTypesSupportSentiment.value,
  );

  const sentimentFilterTooltip = computed(() => {
    if (allBrandsWithoutSentimentAccess.value) {
      const brandNames = brandsWithoutSentimentAccess.value.map((brand) => brand.name);
      return FILTER_TOOLTIPS.BRANDS_CANT_ACCESS_SENTIMENT(brandNames.join(', '));
    }
    if (!messageTypesSupportSentiment.value) {
      return FILTER_TOOLTIPS.SENTIMENT_MESSAGE_TYPE_DISABLED;
    }
    return null;
  });

  function getOptionsForFilter(filterKey) {
    return filterOptions.value[filterKey] || [];
  }

  function getFilterState(filter, options) {
    if (options.length === 0) {
      filter.disabled = true;
      filter.tooltip = FILTER_TOOLTIPS.NO_OPTIONS_AVAILABLE(filter.pluralTitle);
      return filter;
    }

    switch (filter.formKey) {
      case REPORT_COMMUNITY_FILTERS.SENTIMENT:
        filter.disabled = isSentimentFilterDisabled.value;
        filter.tooltip = sentimentFilterTooltip.value;
        break;
      default:
        filter.disabled = false;
        filter.tooltip = null;
        break;
    }

    return filter;
  }

  const communityFilters = computed(() => {
    const baseFilters = REPORT_PANEL_OPTIONS.FILTERS.COMMUNITY_FILTERS.filters;
    return baseFilters.map((filter) => {
      const options = getOptionsForFilter(filter.formKey);
      const filteredFilter = getFilterState(filter, options);
      return {
        ...filter,
        options,
        ...filteredFilter,
      };
    });
  });

  const listeningMediaTypeFilterOptions = computed(() =>
    Object.values(MEDIA_TYPE_FILTER_OPTIONS).map((option) => ({
      label: option.label,
      value: option.value,
    })),
  );

  const listeningMentionTypeFilterOptions = computed(() => {
    const options = Object.values(MENTION_TYPE_FILTER_OPTIONS).filter(
      (option) => commentsEnabled.value || !['COMMENT', 'UGC'].includes(option.value),
    );
    return options.map((option) => ({ label: option.label, value: option.value }));
  });

  const listeningSentimentFilterOptions = computed(() =>
    Object.values(LISTENING_SENTIMENT_FILTER_OPTIONS).map((option) => ({
      label: option.label,
      value: option.value,
      severity: option.severity,
    })),
  );

  const locationFilterOptions = computed(
    () => dashboardsSocialListeningStore.socialListeningLocations || [],
  );

  const competitiveChannelTypes = computed(() => {
    if (!channels.value?.length) return [];

    return channels.value
      .map((channel) => {
        const competitiveType = getChannelConfigProp(channel, 'competitiveChannelType');
        return competitiveType || channel;
      })
      .filter(Boolean);
  });

  const competitorFilterOptions = computed(() => {
    const channelTypes = competitiveChannelTypes.value;
    if (!channelTypes.length) return [];

    const allCompetitors = getCompetitorList(searchStore.competitors, channelTypes);

    const channelToIconMap = new Map();
    channels.value?.forEach((channel) => {
      const competitiveType = getChannelConfigProp(channel, 'competitiveChannelType') || channel;
      const icon =
        getChannelConfigProp(channel, 'dashingUiChannelIcon') ??
        getChannelConfigProp(channel, 'channelIcon');
      if (icon) {
        channelToIconMap.set(competitiveType, icon);
      }
    });

    return allCompetitors.map((competitor) => {
      const channel = channelToIconMap.get(competitor.source) || null;
      return {
        label: competitor.name,
        value: `${channel.toUpperCase()}:${competitor.sourceAccountId}`,
        avatar: competitor.avatarUrl,
        icon: competitor.source,
        channel,
      };
    });
  });

  // Listening backend accepts industry ids with no follower bucket appended
  const industryFilterOptions = computed(() => {
    const industries = dashboardsStore.industries || [];
    const uniqueIndustries = new Set();
    return industries.reduce((acc, industry) => {
      if (!uniqueIndustries.has(industry.id)) {
        uniqueIndustries.add(industry.id);
        acc.push({ label: industry.name, value: industry.id });
      }
      return acc;
    }, []);
  });

  const listeningFilterOptions = computed(() => ({
    [REPORT_LISTENING_FILTERS.SENTIMENT]: listeningSentimentFilterOptions.value,
    [REPORT_LISTENING_FILTERS.MEDIA_TYPE]: listeningMediaTypeFilterOptions.value,
    [REPORT_LISTENING_FILTERS.LOCATION]: locationFilterOptions.value,
    [REPORT_LISTENING_FILTERS.MENTION_TYPE]: listeningMentionTypeFilterOptions.value,
    [REPORT_LISTENING_FILTERS.COMPETITOR]: competitorFilterOptions.value,
    [REPORT_LISTENING_FILTERS.INDUSTRY]: industryFilterOptions.value,
  }));

  function getOptionsForListeningFilter(filterKey) {
    return listeningFilterOptions.value[filterKey] || [];
  }

  function getListeningFilterState(filter, options) {
    if (options.length === 0) {
      filter.disabled = true;
      filter.tooltip = FILTER_TOOLTIPS.NO_OPTIONS_AVAILABLE(filter.pluralTitle);
    } else {
      filter.disabled = false;
      filter.tooltip = null;
    }

    return filter;
  }

  const listeningFilters = computed(() => {
    const baseFilters = REPORT_PANEL_OPTIONS.FILTERS.LISTENING_FILTERS.filters;
    return baseFilters.map((filter) => {
      const options = getOptionsForListeningFilter(filter.formKey);
      const filteredFilter = getListeningFilterState({ ...filter }, options);
      return {
        ...filter,
        options,
        ...filteredFilter,
      };
    });
  });

  const FILTER_CATEGORY_MAP = {
    [REPORT_CATEGORY_OPTIONS.COMMUNITY.id]: () => communityFilters.value,
    [REPORT_CATEGORY_OPTIONS.SOCIAL_LISTENING.id]: () => listeningFilters.value,
  };

  const filtersToShow = computed(() => {
    const getFilters = FILTER_CATEGORY_MAP[category.value];
    return getFilters ? getFilters() : [];
  });

  const FORM_KEY_CATEGORY_MAP = {
    [REPORT_CATEGORY_OPTIONS.COMMUNITY.id]: REPORT_PANEL_OPTIONS.FILTERS.COMMUNITY_FILTERS.formKey,
    [REPORT_CATEGORY_OPTIONS.SOCIAL_LISTENING.id]:
      REPORT_PANEL_OPTIONS.FILTERS.LISTENING_FILTERS.formKey,
  };

  const baseFormKey = computed(() => FORM_KEY_CATEGORY_MAP[category.value] ?? null);

  return {
    filtersToShow,
    baseFormKey,
    messageTagFilterOptions,
  };
}
